import React, { useState, useEffect } from 'react';
import { 
  X, Settings, Copy, Edit3, Trash2, Eye, Users, Calendar, 
  CheckCircle, Clock, Layers, ListChecks, FileText, TrendingUp,
  Globe, Building2, Download, UserPlus, Activity, Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

/**
 * VPG Global Library Assessment Management Modal (Superuser Only)
 * Shows detailed assessment info, usage stats, and management controls
 */
const VPGManagementModal = ({ 
  assessment, 
  onClose, 
  onEdit, 
  onDelete, 
  onCopyToLibrary,
  formatDate 
}) => {
  const [activeTab, setActiveTab] = useState('overview'); // overview | usage | settings
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usageData, setUsageData] = useState({ studies: [], users: [], changeLog: [] });
  
  // Fetch usage data when tab is selected
  useEffect(() => {
    if (activeTab === 'usage' && assessment?.id) {
      fetchUsageData();
    }
  }, [activeTab, assessment?.id]);
  
  const fetchUsageData = async () => {
    setLoadingUsage(true);
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessment.uuid}/usage/`);
      setUsageData(response.data);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setLoadingUsage(false);
    }
  };
  
  // Helper to get user initials
  const getInitials = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user.email?.substring(0, 2).toUpperCase() || 'U';
  };
  
  // Helper to get display name
  const getDisplayName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.email?.split('@')[0] || 'Unknown User';
  };
  
  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (!assessment) return null;

  // Get screens data
  const screens = assessment.survey?.screens || assessment.screens || [];
  const totalScreens = screens.length;
  // Use question_count from API which counts ALL assessment items, not just items placed on screens
  const totalItems = assessment.question_count || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">VPG Library Management</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">Manage global assessment template</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <div className="px-6 flex space-x-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'overview'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 dark:text-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Overview
              </div>
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`px-4 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'usage'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 dark:text-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Usage & Users
              </div>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'settings'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 dark:text-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Settings
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              
              {/* Assessment Info Card */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 rounded-lg p-5 border border-purple-100 dark:border-purple-900/40">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{assessment.name || assessment.survey?.name}</h3>
                
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Version:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">v{assessment.version}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Library:</span>
                    <span className="ml-2 font-medium text-purple-600 capitalize">
                      <Globe className="w-3.5 h-3.5 inline mr-1" />
                      VPG Global
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Status:</span>
                    <span className={`ml-2 font-medium ${
                      assessment.publication_status === 'published' 
                        ? 'text-green-600' 
                        : 'text-amber-600'
                    }`}>
                      {assessment.publication_status === 'published' ? (
                        <><CheckCircle className="w-3.5 h-3.5 inline mr-1" />Published</>
                      ) : (
                        <><Clock className="w-3.5 h-3.5 inline mr-1" />Draft</>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Type:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100 capitalize">
                      {assessment.assessment_type?.replace('_', ' ') || 'Fixed Form'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Created:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                      {formatDate(assessment.created_at)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Modified:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                      {formatDate(assessment.modified || assessment.builder_metadata?.last_saved)}
                    </span>
                  </div>
                </div>
                
                {assessment.description && (
                  <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-900/40">
                    <p className="text-sm text-gray-700 dark:text-gray-300">{assessment.description}</p>
                  </div>
                )}
              </div>

              {/* Survey Structure */}
              {totalScreens > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                  <div className="flex items-center space-x-2 mb-4">
                    <Layers className="w-5 h-5 text-primary-600" />
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">Survey Structure</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-5">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-900/40">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Total Screens</span>
                        <span className="text-2xl font-bold text-blue-600">{totalScreens}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Assessment pages</p>
                    </div>
                    
                    <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Total Items</span>
                        <span className="text-2xl font-bold text-primary-600">{totalItems}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Questions & items</p>
                    </div>
                  </div>

                  {/* Screen Breakdown */}
                  <div className="pt-5 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-2 mb-4">
                      <ListChecks className="w-5 h-5 text-purple-600" />
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">Screen Breakdown</h4>
                    </div>
                    
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {screens.map((screen, index) => (
                        <div 
                          key={screen.id || index}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-semibold text-purple-600">
                                {screen.order_number || index + 1}
                              </span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {screen.title || screen.name || `Screen ${index + 1}`}
                              </p>
                              {screen.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-md">
                                  {screen.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <span className="px-2 py-1 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                              {screen.questions?.length || screen.items?.length || 0} items
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              {assessment.therapeutic_area && (
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">Additional Information</h4>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Therapeutic Area:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{assessment.therapeutic_area}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Usage & Users Tab */}
          {activeTab === 'usage' && (
            <div className="space-y-6">
              {loadingUsage ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                </div>
              ) : (
                <>
                  {/* VPG Template Info */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Global Template
                      </h4>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 rounded-lg border border-purple-200 dark:border-purple-900/40 p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Globe className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100 mb-1">VPG Library Template</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            This is a validated global instrument template. Sponsors can copy this 
                            assessment to their library and customize it for their studies.
                          </p>
                          <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <Copy className="w-3.5 h-3.5" />
                              Use "Copy to Library" to adopt
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Users with Access */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Users with Access
                      </h4>
                    </div>
                    
                    {usageData.users?.length > 0 ? (
                      <div className="space-y-2">
                        {usageData.users.map((user) => (
                          <div 
                            key={user.id}
                            className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                                <span className="text-sm font-semibold text-purple-600">
                                  {getInitials(user)}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900 dark:text-gray-100">{getDisplayName(user)}</p>
                                  {user.permission_level === 'creator' && (
                                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                                      Owner
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                user.permission_level === 'creator' || user.permission_level === 'Full Admin'
                                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700'
                                  : user.permission_level === 'Edit Access' || user.permission_level === 'edit'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                              }`}>
                                {user.permission_level === 'creator' ? 'Full Admin' : user.permission_level || 'View'}
                              </span>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{user.role_display || 'User'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700">
                        <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">No users with explicit access</p>
                      </div>
                    )}
                  </div>

                  {/* Version History */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Version History
                      </h4>
                    </div>
                    
                    {usageData.changeLog?.length > 0 ? (
                      <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <div className="space-y-3 max-h-48 overflow-y-auto">
                          {usageData.changeLog.map((change, index) => (
                            <div key={index} className="flex items-start gap-3">
                              <div className="w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-xs font-medium text-purple-600">
                                  {change.version_number?.split('.')[0] || 'v'}
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{change.action}</p>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatRelativeTime(change.timestamp)}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                                  {change.description}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  by {change.user_name}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700">
                        <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">No version history available</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Management Actions</h3>
                </div>
                
                <div className="space-y-3">
                  {/* Edit Button */}
                  <button
                    onClick={() => {
                      onEdit(assessment);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 border border-blue-200 dark:border-blue-900/40 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 group-hover:bg-blue-200 rounded-lg flex items-center justify-center transition-colors">
                        <Edit3 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-gray-900 dark:text-gray-100">Edit Assessment</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">Modify content and structure</div>
                      </div>
                    </div>
                  </button>

                  {/* Copy Button */}
                  <button
                    onClick={() => {
                      onCopyToLibrary(assessment);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between p-4 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 group-hover:bg-primary-200 rounded-lg flex items-center justify-center transition-colors">
                        <Copy className="w-5 h-5 text-primary-600" />
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-gray-900 dark:text-gray-100">Copy to Sponsor Library</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">Distribute to sponsor organizations</div>
                      </div>
                    </div>
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete "${assessment.name || assessment.survey?.name}"? This action cannot be undone.`)) {
                        onDelete(assessment);
                        onClose();
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 border border-red-200 dark:border-red-900/40 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 group-hover:bg-red-200 rounded-lg flex items-center justify-center transition-colors">
                        <Trash2 className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-gray-900 dark:text-gray-100">Delete Assessment</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">Permanently remove from VPG library</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border-2 border-red-200 dark:border-red-900/40 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold">!</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">Superuser Actions Only</h4>
                    <p className="text-sm text-red-700">
                      These management controls are only visible to superusers. Changes to VPG Global Library 
                      assessments affect all sponsors and studies that use these templates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 dark:bg-gray-900 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                onCopyToLibrary(assessment);
                onClose();
              }}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy to Library
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VPGManagementModal;
